import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import type { Event } from '@/types'

// #292 — mark events read for the current user, with an optimistic update across every cached
// events list (the dashboard's recent page and the Events page's infinite feed) and the unread
// count, and a rollback on error. The lists are deliberately NOT refetched: on "Unread only" a
// refetch would drop the event the popup is showing; the next poll or live event reconciles.

interface EventPage {
  results: Event[]
  [k: string]: unknown
}
interface InfiniteEvents {
  pages: EventPage[]
  [k: string]: unknown
}
type Snapshot = Array<[QueryKey, unknown]>

function isPage(v: unknown): v is EventPage {
  return !!v && typeof v === 'object' && Array.isArray((v as { results?: unknown }).results)
}
function isInfiniteEvents(v: unknown): v is InfiniteEvents {
  return !!v && typeof v === 'object' && Array.isArray((v as { pages?: unknown }).pages)
}

/** Apply `fn` to every cached event in every ['events', …] list. */
function mapCachedEvents(queryClient: QueryClient, fn: (e: Event) => Event) {
  const mapPage = (p: EventPage): EventPage => ({ ...p, results: p.results.map(fn) })
  queryClient.setQueriesData({ queryKey: ['events'] }, (old: unknown) => {
    if (isInfiniteEvents(old)) return { ...old, pages: old.pages.map(mapPage) }
    if (isPage(old)) return mapPage(old)
    return old
  })
}

/** #304 — the unread card does not count motion-only events. */
const countedUnread = (e: Event) => !!e.is_unread && !e.is_motion_only

function cachedCountedUnread(queryClient: QueryClient, eventId: number): boolean {
  return queryClient.getQueriesData({ queryKey: ['events'] }).some(([, data]) => {
    const pages = isInfiniteEvents(data) ? data.pages : isPage(data) ? [data] : []
    return pages.some((p) => p.results.some((e) => e.id === eventId && countedUnread(e)))
  })
}

async function snapshot(queryClient: QueryClient): Promise<Snapshot> {
  await queryClient.cancelQueries({ queryKey: ['events'] })
  return queryClient.getQueriesData({ queryKey: ['events'] })
}

function restore(queryClient: QueryClient, prev: Snapshot | undefined) {
  prev?.forEach(([key, data]) => queryClient.setQueryData(key, data))
}

type UnreadCount = { count: number; as_of?: string }

export function useMarkEventRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) => eventsApi.markAsRead(eventId),
    onMutate: async (eventId: number) => {
      const wasUnread = cachedCountedUnread(queryClient, eventId)
      const prev = await snapshot(queryClient)
      mapCachedEvents(queryClient, (e) => (e.id === eventId ? { ...e, is_unread: false } : e))
      if (wasUnread) {
        // keep as_of: "mark all" sends it, even before the count is refetched
        queryClient.setQueryData<UnreadCount>(queryKeys.events.unreadCount(), (old) =>
          old ? { ...old, count: Math.max(0, old.count - 1) } : old)
      }
      return { prev }
    },
    onError: (_err, _id, ctx) => restore(queryClient, ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.unreadCount() })
    },
  })
}

/** Unread rows the card counts that arrived after `asOf`, counted once across every cached list. */
function newerUnreadCount(queryClient: QueryClient, asOf: number): number {
  const ids = new Set<number>()
  queryClient.getQueriesData({ queryKey: ['events'] }).forEach(([, data]) => {
    const pages = isInfiniteEvents(data) ? data.pages : isPage(data) ? [data] : []
    pages.forEach((p) => p.results.forEach((e) => {
      if (countedUnread(e) && Date.parse(e.occurred_at) > asOf) ids.add(e.id)
    }))
  })
  return ids.size
}

export function useMarkAllEventsRead() {
  const queryClient = useQueryClient()
  // The as_of of the count on screen when the button was pressed: only events up to it are
  // marked (an event that arrived after the screen's last count stays unread).
  const lastAsOf = () => queryClient.getQueryData<UnreadCount>(queryKeys.events.unreadCount())?.as_of
  const mutation = useMutation({
    mutationFn: (upTo: string | undefined) => eventsApi.markAllAsRead(upTo),
    onMutate: async (upTo: string | undefined) => {
      const prev = await snapshot(queryClient)
      const cut = upTo ? Date.parse(upTo) : NaN
      const covered = (e: Event) => Number.isNaN(cut) || !(Date.parse(e.occurred_at) > cut)
      mapCachedEvents(queryClient, (e) => (e.is_unread && covered(e) ? { ...e, is_unread: false } : e))
      queryClient.setQueryData<UnreadCount>(queryKeys.events.unreadCount(), (old) => ({
        ...old,
        count: Number.isNaN(cut) ? 0 : newerUnreadCount(queryClient, cut),
      }))
      return { prev }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UnreadCount>(queryKeys.events.unreadCount(), (old) => ({ ...old, count: data.count }))
    },
    onError: (_err, _v, ctx) => restore(queryClient, ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.unreadCount() })
    },
  })
  // Callers press the button with no argument; the up_to is read at that moment.
  return { ...mutation, mutate: () => mutation.mutate(lastAsOf()) }
}
